/**
 * Dữ liệu mẫu của phân hệ Gói dịch vụ.
 *
 * Danh mục dịch vụ **chép đúng seed** của `db/control-plane/migrations/V2__goi_dich_vu.sql`
 * (ADR-0019 mục 2.1): 10 nhóm điều hướng của Portal, hai cái bắt buộc, và cặp
 * `tri-thuc` dùng chung giữa Tri thức AI với Trợ lý AI. Lệch seed là màn hình
 * diễn một hệ thống không tồn tại.
 *
 * ⚠️ Tầng này bị xoá khi API thật về. **Không** đặt màu hay nhãn hiển thị ở đây
 * — chúng ở `modules/goi-dich-vu/constants.ts` và catalog i18n.
 */
import type { ApiError } from '../lib/errors';
import { ngayVN } from '../lib/dinh-dang';
import type { DichVu, GhiDe, KyThueBao } from '../modules/goi-dich-vu/nghiep-vu/types';

export const MOCK_DICH_VU: DichVu[] = [
  { ma: 'tong-quan', ten: 'Tổng quan', mo_ta: 'Trang đích sau đăng nhập, báo cáo thống kê và thông báo.', thu_tu: 1, bat_buoc: true, vung_api: [] },
  { ma: 'cau-hinh-he-thong', ten: 'Cấu hình hệ thống', mo_ta: 'Vai trò, người dùng, cơ cấu tổ chức.', thu_tu: 2, bat_buoc: true, vung_api: [] },
  { ma: 'tri-thuc-ai', ten: 'Tri thức AI', mo_ta: 'Quản lý tri thức, kiểm duyệt và phê duyệt.', thu_tu: 3, bat_buoc: false, vung_api: ['tri-thuc'] },
  { ma: 'xu-ly-tai-lieu', ten: 'Xử lý tài liệu', mo_ta: 'Nhận dạng OCR, quản lý tài liệu và chứng từ.', thu_tu: 4, bat_buoc: false, vung_api: ['ocr'] },
  { ma: 'xu-ly-van-ban', ten: 'Xử lý văn bản', mo_ta: 'Tóm tắt, soạn thảo, phân công và quản lý nhiệm vụ.', thu_tu: 5, bat_buoc: false, vung_api: ['van-ban', 'nhiem-vu'] },
  { ma: 'tro-ly-ai', ten: 'Trợ lý AI', mo_ta: 'Hỏi đáp AI, chatbot và nguồn dữ liệu.', thu_tu: 6, bat_buoc: false, vung_api: ['tro-ly-ai', 'tri-thuc'] },
  { ma: 'tham-dinh-ai', ten: 'Thẩm định AI', mo_ta: 'Hồ sơ thẩm định, phê duyệt và thư viện tiêu chí.', thu_tu: 7, bat_buoc: false, vung_api: ['tham-dinh'] },
  { ma: 'dinh-danh-ekyc', ten: 'Định danh eKYC', mo_ta: 'Định danh người dùng, hồ sơ và quy trình định danh.', thu_tu: 8, bat_buoc: false, vung_api: ['ekyc'] },
  { ma: 'bao-cao-doanh-nghiep', ten: 'Báo cáo doanh nghiệp', mo_ta: 'Báo cáo tài chính, dự án, kinh doanh và nhân sự.', thu_tu: 9, bat_buoc: false, vung_api: ['bao-cao'] },
  { ma: 'quan-ly-rpa', ten: 'Quản lý RPA', mo_ta: 'Project, workflow, lịch sử chạy và license RPA.', thu_tu: 10, bat_buoc: false, vung_api: ['rpa'] },
];

/** Phần lưu được của một gói; `dich_vu`, `so_tenant_dang_dung`, `so_ky` tính lúc đọc. */
export interface MockGoi {
  ma: string;
  ten: string;
  mo_ta: string;
  an_han_ngay_mac_dinh: number;
  hoat_dong: boolean;
  he_thong: boolean;
  dich_vu: string[];
  ngay_tao: string;
  ngay_cap_nhat: string;
}

export const MOCK_GOI: MockGoi[] = [
  {
    ma: 'TOAN_DIEN',
    ten: 'Toàn diện',
    mo_ta: 'Đủ mọi dịch vụ. Gói của các tenant có từ trước khi quản lý thuê bao.',
    an_han_ngay_mac_dinh: 15,
    hoat_dong: true,
    he_thong: true,
    dich_vu: MOCK_DICH_VU.filter((d) => !d.bat_buoc).map((d) => d.ma),
    ngay_tao: '2026-09-01T10:00:00+07:00',
    ngay_cap_nhat: '2026-09-01T10:00:00+07:00',
  },
  {
    ma: 'CO_BAN',
    ten: 'Cơ bản',
    mo_ta: 'Gói cho doanh nghiệp nhỏ: xử lý tài liệu và văn bản.',
    an_han_ngay_mac_dinh: 15,
    hoat_dong: true,
    he_thong: false,
    dich_vu: ['xu-ly-tai-lieu', 'xu-ly-van-ban'],
    ngay_tao: '2026-09-15T09:30:00+07:00',
    ngay_cap_nhat: '2026-10-02T14:05:00+07:00',
  },
  {
    ma: 'NANG_CAO',
    ten: 'Nâng cao',
    mo_ta: 'Thêm Trợ lý AI, Tri thức AI và báo cáo doanh nghiệp.',
    an_han_ngay_mac_dinh: 30,
    hoat_dong: true,
    he_thong: false,
    dich_vu: ['xu-ly-tai-lieu', 'xu-ly-van-ban', 'tro-ly-ai', 'tri-thuc-ai', 'bao-cao-doanh-nghiep'],
    ngay_tao: '2026-09-15T09:35:00+07:00',
    ngay_cap_nhat: '2026-10-10T08:20:00+07:00',
  },
  {
    ma: 'THAM_DINH',
    ten: 'Thẩm định chuyên biệt',
    mo_ta: 'Gói cho đơn vị thẩm định: hồ sơ, tiêu chí và eKYC.',
    an_han_ngay_mac_dinh: 7,
    hoat_dong: true,
    he_thong: false,
    dich_vu: ['tham-dinh-ai', 'dinh-danh-ekyc', 'xu-ly-tai-lieu'],
    ngay_tao: '2026-09-28T11:00:00+07:00',
    ngay_cap_nhat: '2026-09-28T11:00:00+07:00',
  },
  {
    ma: 'DUNG_THU',
    ten: 'Dùng thử 30 ngày',
    mo_ta: 'Gói cũ, đã ngừng bán. Các kỳ đang chạy vẫn chạy tới hết.',
    an_han_ngay_mac_dinh: 0,
    hoat_dong: false,
    he_thong: false,
    dich_vu: ['xu-ly-tai-lieu', 'tro-ly-ai'],
    ngay_tao: '2026-06-01T08:00:00+07:00',
    ngay_cap_nhat: '2026-09-20T16:45:00+07:00',
  },
  {
    ma: 'RPA_RIENG',
    ten: 'RPA riêng',
    mo_ta: 'Gói mới, chưa bán cho tenant nào — xoá được.',
    an_han_ngay_mac_dinh: 15,
    hoat_dong: true,
    he_thong: false,
    dich_vu: ['quan-ly-rpa'],
    ngay_tao: '2026-10-14T15:00:00+07:00',
    ngay_cap_nhat: '2026-10-14T15:00:00+07:00',
  },
];

/**
 * Kỳ thuê bao của các tenant, gom theo `tenant_id` — đúng `tenant_id` của
 * `mock/tenant.ts`, để tab "Dịch vụ & thuê bao" ở màn chi tiết có dữ liệu.
 *
 * Dựng để **mỗi tình trạng của BR-3xx đều có mặt** trên màn hình: hiệu lực, sắp
 * hết hạn, ân hạn, hết hạn, chưa bắt đầu, và một tenant chưa có kỳ nào. Có cả
 * một kỳ đã hủy và một kỳ bị cắt đuôi vì đổi gói, để cột "Tenant đang dùng"
 * (BR-105) có dữ liệu thật sự khó.
 */
export const MOCK_KY: Record<string, KyThueBao[]> = {
  // Hiệu lực dài, gói Nâng cao.
  '0a7d3c5e-1f2b-4e6a-9c8d-000000000001': [
    { id: 101, ma_goi: 'NANG_CAO', bat_dau: '2026-01-01', ket_thuc: '2027-06-30', an_han_ngay: 30, trang_thai: 'hieu_luc', so_hop_dong: 'HD-2026-011', ghi_chu: '', tac_nhan: 'console:admin', huy_luc: null, ly_do_huy: null },
  ],
  // Không thời hạn — tenant chuyển đổi từ trước.
  '0a7d3c5e-1f2b-4e6a-9c8d-000000000002': [
    { id: 102, ma_goi: 'TOAN_DIEN', bat_dau: '2025-11-01', ket_thuc: null, an_han_ngay: 15, trang_thai: 'hieu_luc', so_hop_dong: null, ghi_chu: 'Chuyển đổi khi bật quản lý thuê bao', tac_nhan: 'migration', huy_luc: null, ly_do_huy: null },
  ],
  // Sắp hết hạn: còn trong ngưỡng 30 ngày.
  '0a7d3c5e-1f2b-4e6a-9c8d-000000000003': [
    { id: 103, ma_goi: 'CO_BAN', bat_dau: '2026-04-01', ket_thuc: '2026-11-05', an_han_ngay: 15, trang_thai: 'hieu_luc', so_hop_dong: 'HD-2026-017', ghi_chu: '', tac_nhan: 'console:admin', huy_luc: null, ly_do_huy: null },
  ],
  // Đang ân hạn: đã quá ket_thuc nhưng còn trong 15 ngày.
  '0a7d3c5e-1f2b-4e6a-9c8d-000000000004': [
    { id: 104, ma_goi: 'CO_BAN', bat_dau: '2026-03-01', ket_thuc: '2026-10-08', an_han_ngay: 15, trang_thai: 'hieu_luc', so_hop_dong: 'HD-2026-004', ghi_chu: 'Đang đàm phán gia hạn', tac_nhan: 'console:admin', huy_luc: null, ly_do_huy: null },
  ],
  // Hết hạn hẳn: ân hạn 0 nên hết hạn ngay hôm sau ket_thuc.
  '0a7d3c5e-1f2b-4e6a-9c8d-000000000005': [
    { id: 105, ma_goi: 'DUNG_THU', bat_dau: '2026-08-01', ket_thuc: '2026-08-31', an_han_ngay: 0, trang_thai: 'hieu_luc', so_hop_dong: null, ghi_chu: 'Hết hạn dùng thử', tac_nhan: 'he_thong', huy_luc: null, ly_do_huy: null },
  ],
  // Đổi gói giữa kỳ: kỳ CO_BAN bị cắt đuôi, kỳ NANG_CAO đang chạy.
  // Cột "Tenant đang dùng" của CO_BAN KHÔNG được đếm tenant này (BR-105).
  '0a7d3c5e-1f2b-4e6a-9c8d-000000000006': [
    { id: 106, ma_goi: 'CO_BAN', bat_dau: '2026-01-01', ket_thuc: '2026-09-30', an_han_ngay: 15, trang_thai: 'hieu_luc', so_hop_dong: 'HD-2026-021', ghi_chu: '', tac_nhan: 'console:admin', huy_luc: null, ly_do_huy: null },
    { id: 107, ma_goi: 'NANG_CAO', bat_dau: '2026-10-01', ket_thuc: '2027-09-30', an_han_ngay: 30, trang_thai: 'hieu_luc', so_hop_dong: 'HD-2026-021-SD1', ghi_chu: 'Nâng gói theo đề nghị', tac_nhan: 'console:admin', huy_luc: null, ly_do_huy: null },
  ],
  // Chưa bắt đầu: kỳ ký trước, chạy từ tháng sau.
  '0a7d3c5e-1f2b-4e6a-9c8d-000000000007': [
    { id: 108, ma_goi: 'THAM_DINH', bat_dau: '2026-11-01', ket_thuc: '2027-10-31', an_han_ngay: 7, trang_thai: 'hieu_luc', so_hop_dong: 'HD-2026-033', ghi_chu: '', tac_nhan: 'console:admin', huy_luc: null, ly_do_huy: null },
  ],
  // Có kỳ đã hủy: không tính vào "đang dùng" nhưng vẫn là lịch sử (so_ky).
  '0a7d3c5e-1f2b-4e6a-9c8d-000000000008': [
    { id: 109, ma_goi: 'THAM_DINH', bat_dau: '2026-05-01', ket_thuc: '2027-04-30', an_han_ngay: 7, trang_thai: 'da_huy', so_hop_dong: 'HD-2026-028', ghi_chu: '', tac_nhan: 'console:admin', huy_luc: '2026-07-15T10:00:00+07:00', ly_do_huy: 'Doanh nghiệp chấm dứt hợp đồng' },
  ],
  // Tenant chưa có kỳ nào.
  '0a7d3c5e-1f2b-4e6a-9c8d-000000000009': [],
};

/** Ghi đè theo tenant — để màn hình có cả `ghi_de_bat` và `ghi_de_tat`. */
export const MOCK_GHI_DE: Record<string, GhiDe[]> = {
  '0a7d3c5e-1f2b-4e6a-9c8d-000000000003': [
    { ma_dich_vu: 'tro-ly-ai', che_do: 'bat', tu: '2026-10-01', den: '2026-12-31', ghi_chu: 'Dùng thử 3 tháng', tac_nhan: 'console:admin', ngay_cap_nhat: '2026-10-01T09:00:00+07:00' },
  ],
  '0a7d3c5e-1f2b-4e6a-9c8d-000000000001': [
    { ma_dich_vu: 'bao-cao-doanh-nghiep', che_do: 'tat', tu: null, den: null, ghi_chu: 'Tenant yêu cầu tắt', tac_nhan: 'console:admin', ngay_cap_nhat: '2026-09-12T14:30:00+07:00' },
  ],
};

// ================================================================================
// Máy chủ giả cho sáu endpoint thuê bao và ghi đè (6–11 của API_CONTRACT.md).
//
// Giữ **đúng luật ghi của backend**, không chỉ trả dữ liệu đẹp: form phải chạy
// được cả nhánh lỗi lúc còn mock, nếu không thì nhánh đó chỉ lộ ra lúc nối API
// thật. Ba luật đắt nhất được cài ở đây: không chồng lấn (BR-201), cắt đuôi kỳ
// khi đổi gói (BR-206, BR-207), và dịch vụ bắt buộc không ghi đè được (BR-401).
// ================================================================================

/** Bản sao trong phiên: thao tác ghi thấy được ngay, như API thật. */
const cacKy: Record<string, KyThueBao[]> = structuredClone(MOCK_KY);
const cacGhiDe: Record<string, GhiDe[]> = structuredClone(MOCK_GHI_DE);

let idKeTiep = 200;

const loiThueBao = (code: string, status: number, message: string): ApiError => ({
  code,
  status,
  message,
});

/** `null` của `ket_thuc` là không thời hạn, nên so sánh coi nó là vô cùng. */
const CUOI_CUNG = '9999-12-31';

/**
 * Giao nhau của hai khoảng ngày, tính **cả hai đầu** (BR-201, BR-309).
 *
 * Phép giao đúng là `a.bat_dau <= b.ket_thuc && b.bat_dau <= a.ket_thuc`. Viết
 * sai thành `<` ở một đầu là để lọt hai kỳ kề nhau đúng một ngày.
 */
const giaoNhau = (
  aBatDau: string,
  aKetThuc: string | null,
  bBatDau: string,
  bKetThuc: string | null,
): boolean => aBatDau <= (bKetThuc ?? CUOI_CUNG) && bBatDau <= (aKetThuc ?? CUOI_CUNG);

const dsKy = (tenantId: string): KyThueBao[] => (cacKy[tenantId] ??= []);

const dsGhiDe = (tenantId: string): GhiDe[] => (cacGhiDe[tenantId] ??= []);

function timKy(tenantId: string, id: number): KyThueBao {
  const ky = dsKy(tenantId).find((k) => k.id === id);
  if (!ky) {
    throw loiThueBao('SUBSCRIPTION_NOT_FOUND', 404, 'Không có kỳ thuê bao này.');
  }
  return ky;
}

/** Kỳ chưa huỷ nào chồng lên khoảng `[batDau, ketThuc]`, bỏ qua kỳ `boQua` (BR-201). */
function kyChongLan(
  tenantId: string,
  batDau: string,
  ketThuc: string | null,
  boQua?: number,
): KyThueBao | undefined {
  return dsKy(tenantId).find(
    (k) =>
      k.trang_thai === 'hieu_luc' &&
      k.id !== boQua &&
      giaoNhau(batDau, ketThuc, k.bat_dau, k.ket_thuc),
  );
}

/** Kỳ phải cắt đuôi khi đổi gói: đang chạy vào `batDauMoi` và bắt đầu TRƯỚC ngày đó (BR-207). */
function kyBiCat(tenantId: string, batDauMoi: string): KyThueBao | undefined {
  return dsKy(tenantId).find(
    (k) =>
      k.trang_thai === 'hieu_luc' &&
      k.bat_dau < batDauMoi &&
      batDauMoi <= (k.ket_thuc ?? CUOI_CUNG),
  );
}

const doiNgay = (ngay: string, buoc: number): string => {
  const d = new Date(`${ngay}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + buoc);
  return d.toISOString().slice(0, 10);
};

const lui1Ngay = (ngay: string): string => doiNgay(ngay, -1);
const tien1Ngay = (ngay: string): string => doiNgay(ngay, 1);

export const mayChuThueBao = {
  /** `GET /tenants/{id}/dich-vu` — một lời gọi trả đủ dữ liệu tab. */
  cuaTenant: (tenantId: string) => ({
    tenantId,
    cacKy: structuredClone(dsKy(tenantId)),
    cacGhiDe: structuredClone(dsGhiDe(tenantId)),
  }),

  /** `POST /tenants/{id}/thue-bao` — thuê lần đầu, gia hạn, hoặc đổi gói. */
  taoKy: (
    tenantId: string,
    vao: {
      ma_goi: string;
      bat_dau: string;
      ket_thuc: string | null;
      an_han_ngay: number | null;
      so_hop_dong: string | null;
      ghi_chu: string;
      thay_the: boolean;
    },
    goi: { ma: string; ten: string; hoat_dong: boolean; an_han_ngay_mac_dinh: number } | undefined,
  ) => {
    if (!goi) {
      throw loiThueBao('PACKAGE_NOT_FOUND', 404, `Không có gói "${vao.ma_goi}".`);
    }
    if (!goi.hoat_dong) {
      throw loiThueBao('PACKAGE_INACTIVE', 409, `Gói "${goi.ten}" đã ngừng bán, không tạo kỳ mới được.`);
    }
    if (vao.ket_thuc !== null && vao.ket_thuc < vao.bat_dau) {
      throw loiThueBao('DATE_RANGE_INVALID', 400, 'Ngày kết thúc phải từ ngày bắt đầu trở đi.');
    }

    // Đổi gói: kỳ sẽ bị cắt đuôi về `bat_dau - 1` (BR-206). Chỉ cắt kỳ bắt đầu
    // TRƯỚC ngày đó — cắt kỳ cùng ngày là biến nó thành kỳ rỗng (BR-207).
    const cat = vao.thay_the ? kyBiCat(tenantId, vao.bat_dau) : undefined;

    // Kiểm chồng lấn TRƯỚC khi ghi, bỏ qua kỳ sắp bị cắt: cắt rồi mới kiểm thì
    // một lần thất bại để lại kỳ đã bị cắt dở, mà giao dịch thật thì rollback cả.
    const chong = kyChongLan(tenantId, vao.bat_dau, vao.ket_thuc, cat?.id);
    if (chong) {
      // Đã bật "đổi gói" mà vẫn chồng, và kỳ chồng bắt đầu ĐÚNG ngày này: kyBiCat cố ý bỏ qua nó
      // (BR-207). Câu của BR-201 khuyên "chọn đổi gói" nên không dùng được — người dùng vừa làm
      // đúng việc đó. Nêu hai lối đi thật.
      if (vao.thay_the && chong.bat_dau === vao.bat_dau) {
        throw loiThueBao(
          'SUBSCRIPTION_SAME_START_DATE',
          409,
          `Kỳ #${chong.id} cũng bắt đầu ${ngayVN(vao.bat_dau)} nên không cắt đuôi được`
            + ` — cắt sẽ thành kỳ rỗng. Huỷ kỳ #${chong.id}, hoặc đặt ngày bắt đầu từ`
            + ` ${ngayVN(tien1Ngay(vao.bat_dau))}.`,
        );
      }
      throw loiThueBao(
        'SUBSCRIPTION_OVERLAP',
        409,
        `Khoảng ngày chồng lên kỳ #${chong.id} (${ngayVN(chong.bat_dau)} – ${ngayVN(chong.ket_thuc)}).`
          + ' Sửa, huỷ kỳ đó, hoặc chọn "đổi gói" để cắt kỳ đang chạy.',
      );
    }
    if (cat) {
      cat.ket_thuc = lui1Ngay(vao.bat_dau);
    }

    const moi: KyThueBao = {
      id: (idKeTiep += 1),
      ma_goi: vao.ma_goi,
      bat_dau: vao.bat_dau,
      ket_thuc: vao.ket_thuc,
      // Bỏ trống thì CHÉP ân hạn của gói, không tham chiếu (BR-208).
      an_han_ngay: vao.an_han_ngay ?? goi.an_han_ngay_mac_dinh,
      trang_thai: 'hieu_luc',
      so_hop_dong: vao.so_hop_dong?.trim() ? vao.so_hop_dong.trim() : null,
      ghi_chu: vao.ghi_chu,
      tac_nhan: 'console:admin',
      huy_luc: null,
      ly_do_huy: null,
    };
    dsKy(tenantId).push(moi);
    return moi;
  },

  /** `PATCH /tenants/{id}/thue-bao/{idKy}` — sửa điều khoản, không đổi gói (BR-212). */
  suaKy: (
    tenantId: string,
    idKy: number,
    vao: {
      bat_dau?: string;
      ket_thuc?: string | null;
      an_han_ngay?: number;
      so_hop_dong?: string | null;
      ghi_chu?: string;
    },
  ) => {
    const ky = timKy(tenantId, idKy);
    if (ky.trang_thai === 'da_huy') {
      throw loiThueBao('SUBSCRIPTION_CANCELLED', 409, 'Kỳ đã huỷ, không sửa được.');
    }

    // Khoá VẮNG MẶT là giữ nguyên; `ket_thuc: null` tường minh là chuyển sang
    // không thời hạn (BR-213). `in` phân biệt được hai việc đó, `??` thì không.
    const batDau = 'bat_dau' in vao && vao.bat_dau !== undefined ? vao.bat_dau : ky.bat_dau;
    const ketThuc = 'ket_thuc' in vao ? (vao.ket_thuc ?? null) : ky.ket_thuc;

    if (ketThuc !== null && ketThuc < batDau) {
      throw loiThueBao('DATE_RANGE_INVALID', 400, 'Ngày kết thúc phải từ ngày bắt đầu trở đi.');
    }
    const chong = kyChongLan(tenantId, batDau, ketThuc, idKy);
    if (chong) {
      throw loiThueBao(
        'SUBSCRIPTION_OVERLAP',
        409,
        `Khoảng ngày chồng lên kỳ #${chong.id} (${ngayVN(chong.bat_dau)} – ${ngayVN(chong.ket_thuc)}).`
          + ' Sửa, huỷ kỳ đó, hoặc chọn "đổi gói" để cắt kỳ đang chạy.',
      );
    }

    ky.bat_dau = batDau;
    ky.ket_thuc = ketThuc;
    if (vao.an_han_ngay !== undefined) ky.an_han_ngay = vao.an_han_ngay;
    // Xoá trắng số hợp đồng lưu `null`, không lưu chuỗi rỗng (BR-214).
    if ('so_hop_dong' in vao) ky.so_hop_dong = vao.so_hop_dong?.trim() ? vao.so_hop_dong.trim() : null;
    if (vao.ghi_chu !== undefined) ky.ghi_chu = vao.ghi_chu;
    return ky;
  },

  /** `POST /tenants/{id}/thue-bao/{idKy}/huy` — đổi trạng thái, không xoá dòng (BR-211). */
  huyKy: (tenantId: string, idKy: number, lyDo: string) => {
    const ky = timKy(tenantId, idKy);
    if (ky.trang_thai === 'da_huy') {
      throw loiThueBao('SUBSCRIPTION_ALREADY_CANCELLED', 409, 'Kỳ này đã huỷ từ trước.');
    }
    if (!lyDo.trim()) {
      throw loiThueBao('CANCEL_REASON_REQUIRED', 422, 'Cần nêu lý do huỷ.');
    }
    if (lyDo.trim().length > 500) {
      throw loiThueBao('CANCEL_REASON_TOO_LONG', 422, 'Lý do dài tối đa 500 ký tự.');
    }
    ky.trang_thai = 'da_huy';
    ky.huy_luc = new Date().toISOString();
    ky.ly_do_huy = lyDo.trim();
    return ky;
  },

  /** `PUT /tenants/{id}/dich-vu/{ma}` — đặt ghi đè, luỹ đẳng. */
  datGhiDe: (
    tenantId: string,
    maDichVu: string,
    vao: { che_do: 'bat' | 'tat'; tu: string | null; den: string | null; ghi_chu: string },
  ) => {
    const dv = MOCK_DICH_VU.find((d) => d.ma === maDichVu);
    if (!dv) {
      throw loiThueBao('SERVICE_NOT_FOUND', 404, `Không có dịch vụ "${maDichVu}".`);
    }
    if (dv.bat_buoc) {
      throw loiThueBao(
        'SERVICE_MANDATORY',
        409,
        `"${dv.ten}" là dịch vụ bắt buộc, luôn bật — không ghi đè được.`,
      );
    }
    if (vao.tu !== null && vao.den !== null && vao.den < vao.tu) {
      throw loiThueBao('DATE_RANGE_INVALID', 400, 'Ngày kết thúc phải từ ngày bắt đầu trở đi.');
    }

    const ds = dsGhiDe(tenantId);
    const moi: GhiDe = {
      ma_dich_vu: maDichVu,
      che_do: vao.che_do,
      tu: vao.tu,
      den: vao.den,
      ghi_chu: vao.ghi_chu,
      tac_nhan: 'console:admin',
      ngay_cap_nhat: new Date().toISOString(),
    };
    const i = ds.findIndex((g) => g.ma_dich_vu === maDichVu);
    if (i >= 0) ds[i] = moi;
    else ds.push(moi);
    return moi;
  },

  /** `DELETE /tenants/{id}/dich-vu/{ma}` — bỏ ghi đè, trả dịch vụ về theo gói. */
  boGhiDe: (tenantId: string, maDichVu: string) => {
    const ds = dsGhiDe(tenantId);
    const i = ds.findIndex((g) => g.ma_dich_vu === maDichVu);
    if (i < 0) {
      throw loiThueBao(
        'OVERRIDE_NOT_FOUND',
        404,
        'Dịch vụ này đang theo gói, không có ghi đè để bỏ.',
      );
    }
    ds.splice(i, 1);
  },
};
